import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Upload, X, Image, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const FileUpload = ({ 
  type = 'avatar', // 'avatar', 'banner', 'gallery'
  onUploadComplete,
  currentUrl = null,
  className = "",
  accept = "image/*",
  maxSizeMB = 2,
  disabled = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const [error, setError] = useState(null);

  const getUploadEndpoint = () => {
    switch (type) {
      case 'banner': return `${API}/upload/banner`;
      case 'gallery': return `${API}/upload/gallery`;
      default: return `${API}/upload/avatar`;
    }
  };

  const getMaxSize = () => {
    switch (type) {
      case 'banner': return 5; // 5MB
      case 'gallery': return 3; // 3MB  
      default: return 2; // 2MB
    }
  };

  const validateFile = (file) => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPEG, PNG, and WebP images are allowed');
    }

    // Check file size
    const maxBytes = getMaxSize() * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(`File size must be less than ${getMaxSize()}MB`);
    }

    return true;
  };

  const createPreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(getUploadEndpoint(), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  };

  const handleFile = async (file) => {
    setError(null);
    setUploading(true);

    try {
      // Validate file
      validateFile(file);

      // Create preview
      const previewUrl = await createPreview(file);
      setPreview(previewUrl);

      // Upload file
      const result = await uploadFile(file);
      
      toast.success('File uploaded successfully!');
      
      // Call completion callback
      if (onUploadComplete) {
        onUploadComplete(result);
      }

    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to upload file';
      setError(message);
      toast.error(message);
      setPreview(currentUrl); // Reset to current URL on error
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, [disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleInputChange = (e) => {
    if (disabled) return;
    
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const clearFile = () => {
    setPreview(null);
    setError(null);
    if (onUploadComplete) {
      onUploadComplete({ url: null });
    }
  };

  const getAspectRatio = () => {
    switch (type) {
      case 'banner': return 'aspect-[3/1]';
      case 'gallery': return 'aspect-square';
      default: return 'aspect-square';
    }
  };

  const getDimensions = () => {
    switch (type) {
      case 'banner': return 'w-full max-w-2xl';
      case 'gallery': return 'w-48';
      default: return 'w-32';
    }
  };

  return (
    <div className={`${className}`}>
      <Card 
        className={`
          ${getDimensions()} ${getAspectRatio()} 
          border-2 border-dashed transition-all duration-200 cursor-pointer
          ${dragActive ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 hover:border-gray-500'}
          ${error ? 'border-red-500 bg-red-500/10' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${uploading ? 'border-blue-500 bg-blue-500/10' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <CardContent className="p-0 h-full relative">
          <input
            type="file"
            accept={accept}
            onChange={handleInputChange}
            disabled={disabled || uploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />

          {preview ? (
            <div className="relative w-full h-full group">
              <img 
                src={preview.startsWith('http') ? preview : `${BACKEND_URL}${preview}`}
                alt="Upload preview"
                className="w-full h-full object-cover rounded-lg"
              />
              
              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Trigger file input
                      e.currentTarget.parentElement.parentElement.parentElement.querySelector('input[type="file"]').click();
                    }}
                    disabled={disabled || uploading}
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-red-500/80 border-red-500 text-white hover:bg-red-600/80"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFile();
                    }}
                    disabled={disabled || uploading}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Upload indicator */}
              {uploading && (
                <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                    <div className="text-sm">Uploading...</div>
                  </div>
                </div>
              )}

              {/* Status indicators */}
              {!uploading && !error && preview !== currentUrl && (
                <div className="absolute top-2 right-2">
                  <div className="bg-green-500 rounded-full p-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 p-4">
              {uploading ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                  <div className="text-sm">Uploading...</div>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    {error ? (
                      <AlertCircle className="w-8 h-8 text-red-400" />
                    ) : (
                      <Image className="w-8 h-8" />
                    )}
                  </div>
                  
                  <div className="text-center text-sm">
                    {error ? (
                      <div className="text-red-400">
                        <div className="font-medium mb-1">Upload Failed</div>
                        <div className="text-xs">{error}</div>
                      </div>
                    ) : (
                      <>
                        <div className="font-medium mb-1">
                          {dragActive ? 'Drop image here' : `Upload ${type}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          JPEG, PNG, WebP • Max {getMaxSize()}MB
                        </div>
                        {!dragActive && (
                          <div className="mt-2">
                            <Button size="sm" variant="outline" className="pointer-events-none">
                              <Upload className="w-3 h-3 mr-1" />
                              Choose File
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="mt-2 text-red-400 text-xs">
          {error}
        </div>
      )}

      {/* Usage hint */}
      <div className="mt-2 text-xs text-gray-500">
        {type === 'avatar' && 'Square image recommended (e.g. 400x400px)'}
        {type === 'banner' && 'Wide image recommended (e.g. 1200x400px)'}
        {type === 'gallery' && 'Square image for gallery showcase'}
      </div>
    </div>
  );
};

export default FileUpload;